import { Link } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { formatPrice, fetchRegion } from '@/lib/region';
import { resolveBrowserCountry, getCachedBrowserCountry } from '@/lib/geo';
import {
    ArrowLeft, ArrowRight, Heart, Sparkles, Check,
    ThumbsUp, Layers, ShieldCheck, Share2, BookOpen,
    UserPlus, CalendarHeart, Wand2, FileHeart,
} from 'lucide-react';

// Round 48b — Public marketing page at /compatibility.
//
// Goals (per user brief):
//   1) Sell the compatibility feature with depth + craft.
//   2) Show a ready sample reading (we already produced
//      "Rohan & Ananya — Made for Each Other") so visitors can
//      taste the product BEFORE handing over their birth details.
//   3) Surface the live discounted price from /api/pricing/public.
//   4) Route every CTA to /signup (the conversion goal of the page).
//
// Visual language matches Landing.jsx + Partner.jsx — obsidian bg,
// marigold accents, Cormorant serif headings, generous whitespace.

const SAMPLE_PNG = '/img/compat-made-for-each-other.png';

// Pre-rendered sample narration for the on-page card preview. The
// PNG above is the shareable greeting; the card below mirrors the
// same content as it appears inside the app, so visitors see the
// in-app experience as well as the share artefact.
const SAMPLE = {
    personA: 'Rohan',
    personB: 'Ananya',
    verdict_label: 'Made for Each Other',
    cards: [
        {
            id: 'card01',
            title: 'Core Connection',
            icon: 'pass',
            description:
                'Venus in both charts speaks to the same kind of love — warm, expressive, generous. ' +
                'Their Moons sit in compatible nakshatras, so daily life feels easy: small jokes land, ' +
                'silences feel safe, and the rhythm of being together is natural rather than forced.',
        },
        {
            id: 'card02',
            title: 'The Deeper Bond',
            icon: 'pass',
            description:
                'The 7th cuspal sub-lord in both charts is well-significated and connected through ' +
                'star-lord lineage — the doctrinal "promise of marriage" gate is open on both sides. ' +
                'When the chart agrees this clearly, the relationship has a structural foundation, not just chemistry.',
        },
        {
            id: 'card03',
            title: 'Karmic Connection',
            icon: 'mixed',
            description:
                'Rahu in one chart contacts the other\'s Moon — a karmic thread that pulls them together ' +
                'magnetically, but also asks both partners to grow. Expect a few intense passages early on; ' +
                'they are how the bond deepens rather than warning signs.',
        },
        {
            id: 'card04',
            title: 'Individual Harmony',
            icon: 'pass',
            description:
                'Saturn aspects are gentle in both charts, and Mars placements do not clash. Each partner ' +
                'has independent strength — career, friendships, inner life — without that strength turning ' +
                'into rivalry. The "two whole people choosing each other" pattern is clearly present.',
        },
        {
            id: 'card05',
            title: 'Children and Family',
            icon: 'pass',
            description:
                'The 5th house sub-lord in both charts is favourable, and Jupiter\'s placement supports ' +
                'fertility and family expansion. Family-side approval — the 2nd and 4th house axes — also ' +
                'looks clean. The chart points to a calm, supportive home environment.',
        },
    ],
};

const FEATURES = [
    {
        icon: Layers,
        title: 'Five doctrinal layers',
        body:
            'Not a one-number compatibility score. Each reading walks five distinct KP doctrine layers — ' +
            'core connection, deeper bond, karmic thread, individual harmony, family — so you understand ' +
            'where the bond is strong and where it asks for work.',
    },
    {
        icon: ShieldCheck,
        title: 'KP Ruling Planets gate',
        body:
            'Every reading runs through the strict Krishnamurti Paddhati cuspal sub-lord + ruling planet ' +
            'gate. Surface chemistry is not enough — the chart has to actually promise the partnership.',
    },
    {
        icon: BookOpen,
        title: 'Partner-nature lens',
        body:
            'For each layer we tell you what your partner sees from their side of the chart — not just ' +
            'your perspective. Most compatibility apps forget there are two people in the room.',
    },
    {
        icon: Share2,
        title: 'Shareable greeting card',
        body:
            'Every reading produces a beautifully composed PNG with your verdict + five layers — ' +
            'sized for WhatsApp, Instagram, and the family group chat. No watermark spam.',
    },
    {
        icon: Heart,
        title: 'For couples, families and matchmakers',
        body:
            'Whether you are checking your own match, vetting a rishta for your daughter, or just curious — ' +
            'the reading reads the same way: honest, specific, kindly worded.',
    },
];

// Verdict tone styles — mirrors `_verdictTone` from Compatibility.jsx so
// the on-page sample uses the exact same visual language as the app.
const VERDICT_TONE = {
    'Made for Each Other':    'border-emerald-500/40 bg-emerald-500/5 text-emerald-200',
    'A Good Match':           'border-marigold/40 bg-marigold/5 text-marigold',
    'Has Its Challenges':     'border-amber-500/40 bg-amber-500/5 text-amber-200',
    'Needs Careful Thought':  'border-rose-500/40 bg-rose-500/5 text-rose-200',
};

function ThumbBadge({ icon }) {
    const cfg = {
        pass:     { ring: 'bg-emerald-500/15 border-emerald-500/40 text-emerald-300', rotate: '' },
        mixed:    { ring: 'bg-amber-500/15 border-amber-500/40 text-amber-300', rotate: 'rotate-90' },
        friction: { ring: 'bg-rose-500/15 border-rose-500/40 text-rose-300', rotate: 'rotate-180' },
    }[icon] || { ring: 'bg-slate-500/10 border-slate-500/30 text-slate-300', rotate: '' };
    return (
        <span
            className={`inline-flex items-center justify-center w-9 h-9 rounded-full border ${cfg.ring}`}
            data-testid={`sample-icon-${icon}`}
        >
            <ThumbsUp size={18} className={cfg.rotate} strokeWidth={2.2} />
        </span>
    );
}

// Region-aware live pricing for the compatibility product.
//
// For ANONYMOUS visitors (the entire visiting audience for this public
// page) `/api/region/me` 401s and `fetchRegion()` falls back to INR —
// which would lock every visitor into ₹ pricing regardless of where
// they actually are. So we resolve the country DIRECTLY via the
// browser-side ipapi.co lookup (same source the rest of the app uses
// as its primary geo signal), then hit the public pricing endpoint
// with the correct currency. Logged-in visitors get the persisted
// residence_country via `fetchRegion()` as a refinement.
function useCompatibilityPricing() {
    const [data, setData] = useState({
        currency: 'INR',
        list_amount: 500,
        final_amount: 300,
        has_discount: true,
        discount_pct: 40,
        _loading: true,
    });
    useEffect(() => {
        let alive = true;
        (async () => {
            // Step 1 — resolve currency.
            // Default: INR (preserves India as the conservative fallback).
            let currency = 'INR';
            // 1a. Synchronous cache from a prior visit — instant.
            const cached = getCachedBrowserCountry();
            if (cached && cached !== 'IN') currency = 'USD';
            // 1b. Async lookup if no cache (or to refresh).
            try {
                const code = cached || await resolveBrowserCountry();
                if (code && code !== 'IN') currency = 'USD';
                else if (code === 'IN') currency = 'INR';
            } catch { /* keep current currency */ }
            // 1c. Logged-in visitors: prefer the persisted residence_country
            //     so a US user who travelled to India for the week still sees USD.
            const hasToken = typeof window !== 'undefined' && !!localStorage.getItem('ac_token');
            if (hasToken) {
                try {
                    const r = await fetchRegion();
                    if (r?.currency) currency = r.currency;
                } catch { /* keep IP-detected currency */ }
            }
            if (!alive) return;

            // Step 2 — fetch live pricing for that currency.
            try {
                const apiBase = process.env.REACT_APP_BACKEND_URL || '';
                const res = await fetch(`${apiBase}/api/pricing/public?currency=${currency}`);
                if (!res.ok) throw new Error('pricing_fetch_failed');
                const json = await res.json();
                const row = (json?.products || []).find((p) => p.product === 'compatibility');
                if (row && alive) {
                    setData({ ...row, _loading: false });
                    return;
                }
            } catch { /* fall through to defaults below */ }

            // Step 3 — Currency known but the pricing fetch failed. Update
            // the currency on the default row so the UI at least renders
            // the right symbol.
            if (alive) {
                setData((d) => ({
                    ...d,
                    currency,
                    list_amount: currency === 'USD' ? 25 : 500,
                    final_amount: currency === 'USD' ? 15 : 300,
                    _loading: false,
                }));
            }
        })();
        return () => { alive = false; };
    }, []);
    return data;
}

export default function CompatibilityLanding() {
    useEffect(() => {
        const prev = document.title;
        document.title = 'KP Compatibility Reading — AstroConsult';
        return () => { document.title = prev; };
    }, []);

    return (
        <div className="min-h-screen bg-obsidian text-parchment relative overflow-hidden" data-testid="compatibility-landing">
            {/* Decorative starfield */}
            <div
                aria-hidden="true"
                className="pointer-events-none absolute inset-0 opacity-[0.30]"
                style={{
                    backgroundImage:
                        'radial-gradient(circle at 18% 14%, rgba(245,158,11,0.18), transparent 42%),' +
                        'radial-gradient(circle at 82% 38%, rgba(245,158,11,0.10), transparent 45%),' +
                        'radial-gradient(circle at 50% 92%, rgba(245,158,11,0.07), transparent 50%)',
                }}
            />

            <TopNav />
            <Hero />
            <HowItWorksSection />
            <SampleReadingSection />
            <FeaturesSection />
            <PricingSection />
            <FinalCta />
            <Footer />
        </div>
    );
}

function TopNav() {
    return (
        <header className="relative z-10 max-w-7xl mx-auto px-6 py-5 flex items-center justify-between">
            <Link
                to="/"
                className="inline-flex items-center gap-2 text-sm text-slate-300 hover:text-marigold transition-colors"
                data-testid="compat-back-home"
            >
                <ArrowLeft size={16} /> Back to home
            </Link>
            <Link to="/" className="font-serif text-marigold text-xl tracking-wide" data-testid="compat-logo">
                AstroConsult
            </Link>
            <Link to="/login" data-testid="compat-nav-login">
                <Button
                    variant="outline"
                    className="bg-transparent border-marigold/40 text-parchment hover:bg-marigold/10 rounded-sm h-9 px-4 text-sm"
                >
                    Sign in
                </Button>
            </Link>
        </header>
    );
}

function Hero() {
    return (
        <section className="relative z-10 max-w-5xl mx-auto px-6 pt-10 pb-16 md:pt-20 md:pb-24 text-center">
            <div
                className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-marigold/30 bg-marigold/5 text-marigold text-xs tracking-widest uppercase mb-8"
                data-testid="compat-overline"
            >
                <Sparkles size={12} /> KP Compatibility Reading
            </div>
            <h1
                className="font-serif text-4xl sm:text-5xl lg:text-6xl text-parchment leading-tight"
                data-testid="compat-hero-heading"
            >
                Is this the <span className="text-marigold italic">person</span> you are meant to be with?
            </h1>
            <p
                className="mt-6 text-base sm:text-lg text-slate-300 max-w-2xl mx-auto leading-relaxed"
                data-testid="compat-hero-tagline"
            >
                Two birth details. Five doctrinal layers. One honest verdict — the same one a senior KP
                jyotishi would deliver across the table. No vague &ldquo;you complete each other&rdquo; filler.
            </p>
            <div className="mt-10 flex flex-col sm:flex-row gap-3 justify-center" data-testid="compat-hero-ctas">
                <Link to="/signup">
                    <Button
                        className="bg-marigold hover:bg-marigold-hover text-obsidian rounded-sm h-12 px-7 text-base"
                        data-testid="compat-hero-cta-primary"
                    >
                        <Heart size={16} className="mr-2" /> Run your reading
                    </Button>
                </Link>
                <a href="#sample">
                    <Button
                        variant="outline"
                        className="bg-transparent border-marigold/30 text-parchment hover:bg-marigold/10 rounded-sm h-12 px-7 text-base"
                        data-testid="compat-hero-cta-sample"
                    >
                        See a sample reading <ArrowRight size={16} className="ml-2" />
                    </Button>
                </a>
            </div>
        </section>
    );
}

function HowItWorksSection() {
    const steps = [
        {
            icon: UserPlus,
            title: 'Sign up',
            body: 'Free account · email + password. 30 seconds.',
        },
        {
            icon: CalendarHeart,
            title: 'Add both birth details',
            body: 'Date, time, place — yours and your partner\u2019s. Time-of-birth uncertain? Use our Birth-Time Rectification tool first.',
        },
        {
            icon: Wand2,
            title: 'Engine runs the 5 layers',
            body: 'The KP doctrine engine processes both charts — cuspal sub-lords, ruling planets, dasha overlap. Takes about a minute.',
        },
        {
            icon: FileHeart,
            title: 'Read your verdict',
            body: 'Get the verdict, full per-layer breakdown, partner-nature view, and a shareable PNG ready for WhatsApp.',
        },
    ];
    return (
        <section
            className="relative z-10 py-20 md:py-24 border-t border-marigold/10"
            data-testid="compat-how-section"
        >
            <div className="max-w-6xl mx-auto px-6">
                <div className="text-center mb-14">
                    <div className="overline mb-3 text-marigold/80">How it works</div>
                    <h2 className="font-serif text-4xl md:text-5xl text-parchment">
                        Four steps. About <span className="text-marigold">two minutes</span>.
                    </h2>
                </div>
                <ol
                    className="grid md:grid-cols-2 lg:grid-cols-4 gap-5"
                    data-testid="compat-how-steps"
                >
                    {steps.map((s, i) => {
                        const Icon = s.icon;
                        const num = i + 1;
                        return (
                            <li
                                key={s.title}
                                className="relative bg-elevated border border-marigold/15 rounded-sm p-6 hover:border-marigold/40 transition-colors"
                                data-testid={`compat-how-step-${num}`}
                            >
                                <div className="flex items-start justify-between gap-3 mb-4">
                                    <div className="flex items-center justify-center w-11 h-11 rounded-sm bg-marigold/10 border border-marigold/30 text-marigold">
                                        <Icon size={20} />
                                    </div>
                                    <span className="font-serif text-3xl text-marigold/30 leading-none">
                                        {String(num).padStart(2, '0')}
                                    </span>
                                </div>
                                <div className="font-serif text-xl text-parchment mb-2">{s.title}</div>
                                <p className="text-sm text-slate-400 leading-relaxed">{s.body}</p>
                            </li>
                        );
                    })}
                </ol>
                <div className="mt-10 text-center">
                    <Link to="/signup" data-testid="compat-how-cta">
                        <Button className="bg-marigold hover:bg-marigold-hover text-obsidian rounded-sm h-11 px-6">
                            Start step 1 — sign up <ArrowRight size={16} className="ml-2" />
                        </Button>
                    </Link>
                </div>
            </div>
        </section>
    );
}

function SampleReadingSection() {
    const tone = VERDICT_TONE[SAMPLE.verdict_label] || 'border-marigold/30 bg-surface text-parchment';
    return (
        <section
            id="sample"
            className="relative z-10 py-20 md:py-24 border-t border-marigold/10"
            data-testid="compat-sample-section"
        >
            <div className="max-w-6xl mx-auto px-6">
                <div className="text-center mb-14">
                    <div className="overline mb-3 text-marigold/80">Sample Reading</div>
                    <h2 className="font-serif text-4xl md:text-5xl text-parchment">
                        This is what you get.
                    </h2>
                    <p className="mt-4 text-slate-400 max-w-2xl mx-auto">
                        Below is a real reading for Rohan &amp; Ananya. The verdict, the five layers,
                        and a shareable greeting card — exactly as it shows up inside the app.
                    </p>
                </div>

                <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-start">
                    {/* On-page card preview — mirrors CompatSummaryCard styling */}
                    <Card
                        className="bg-elevated border-marigold/15 rounded-sm overflow-hidden"
                        data-testid="compat-sample-card"
                    >
                        <div className="p-5 md:p-6 space-y-5">
                            {/* Verdict block */}
                            <div className={`border ${tone} rounded-sm p-5 text-center`}>
                                <div className="overline opacity-80 mb-1">Verdict</div>
                                <div className="font-serif text-2xl md:text-3xl">{SAMPLE.verdict_label}</div>
                                <div className="text-sm mt-2 opacity-80">
                                    {SAMPLE.personA} <span className="opacity-50 mx-1">&amp;</span> {SAMPLE.personB}
                                </div>
                            </div>

                            {/* 5 layers */}
                            <ul className="divide-y divide-marigold/10 border border-marigold/10 rounded-sm">
                                {SAMPLE.cards.map((c) => (
                                    <li
                                        key={c.id}
                                        className="flex items-start gap-3 px-4 py-4"
                                        data-testid={`compat-sample-${c.id}`}
                                    >
                                        <div className="shrink-0 mt-0.5">
                                            <ThumbBadge icon={c.icon} />
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <div className="font-serif text-marigold text-base">{c.title}</div>
                                            <div className="text-sm text-parchment/85 leading-relaxed mt-1">
                                                {c.description}
                                            </div>
                                        </div>
                                    </li>
                                ))}
                            </ul>

                            <div className="pt-1">
                                <Link to="/signup" data-testid="compat-sample-card-cta">
                                    <Button className="w-full bg-marigold hover:bg-marigold-hover text-obsidian rounded-sm h-11">
                                        Get my own reading <ArrowRight size={16} className="ml-2" />
                                    </Button>
                                </Link>
                            </div>
                        </div>
                    </Card>

                    {/* Shareable PNG */}
                    <div className="lg:sticky lg:top-8">
                        <div className="overline mb-3 text-marigold/80 text-center lg:text-left">
                            Shareable greeting card (PNG)
                        </div>
                        <div className="rounded-sm border border-marigold/20 bg-elevated p-3 md:p-4 shadow-[0_8px_40px_rgba(245,158,11,0.08)]">
                            <img
                                src={SAMPLE_PNG}
                                alt="Sample compatibility greeting card — Rohan & Ananya: Made for Each Other"
                                className="w-full h-auto rounded-sm"
                                data-testid="compat-sample-png"
                                loading="lazy"
                            />
                        </div>
                        <p className="mt-4 text-xs text-slate-500 text-center lg:text-left">
                            Sized for WhatsApp, Instagram and the family group chat · 1200×1500 portrait
                        </p>
                    </div>
                </div>
            </div>
        </section>
    );
}

function FeaturesSection() {
    return (
        <section
            className="relative z-10 py-20 md:py-24 border-t border-marigold/10"
            data-testid="compat-features-section"
        >
            <div className="max-w-6xl mx-auto px-6">
                <div className="text-center mb-14">
                    <div className="overline mb-3 text-marigold/80">Why this reading is different</div>
                    <h2 className="font-serif text-4xl md:text-5xl text-parchment">
                        Built like a real <span className="text-marigold">jyotishi</span> would read it.
                    </h2>
                </div>
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
                    {FEATURES.map((f) => {
                        const Icon = f.icon;
                        return (
                            <Card
                                key={f.title}
                                className="bg-elevated border-marigold/15 rounded-sm p-6 hover:border-marigold/40 transition-colors"
                                data-testid={`compat-feature-${f.title.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
                            >
                                <div className="flex items-center justify-center w-11 h-11 rounded-sm bg-marigold/10 border border-marigold/30 text-marigold mb-4">
                                    <Icon size={20} />
                                </div>
                                <div className="font-serif text-xl text-parchment mb-2">{f.title}</div>
                                <p className="text-sm text-slate-400 leading-relaxed">{f.body}</p>
                            </Card>
                        );
                    })}
                </div>
            </div>
        </section>
    );
}

function PricingSection() {
    const price = useCompatibilityPricing();
    const hasDiscount = price?.has_discount && price?.list_amount > price?.final_amount;
    const finalLabel = formatPrice(price?.final_amount, price?.currency);
    const listLabel = formatPrice(price?.list_amount, price?.currency);

    return (
        <section
            id="pricing"
            className="relative z-10 py-20 md:py-24 border-t border-marigold/10"
            data-testid="compat-pricing-section"
        >
            <div className="max-w-3xl mx-auto px-6">
                <div className="text-center mb-12">
                    <div className="overline mb-3 text-marigold/80">Pricing</div>
                    <h2 className="font-serif text-4xl md:text-5xl text-parchment">
                        One flat price. No subscriptions.
                    </h2>
                    <p className="mt-4 text-slate-400">
                        Pay once per couple, get the full reading + the shareable card. Wallet balance —
                        if you have one — works too.
                    </p>
                </div>

                <Card
                    className="bg-marigold/5 border-marigold rounded-sm p-8 md:p-10 shadow-[0_8px_40px_rgba(245,158,11,0.12)]"
                    data-testid="compat-pricing-card"
                >
                    <div className="text-center">
                        <div className="overline mb-3">Compatibility Reading</div>

                        {hasDiscount ? (
                            <div className="flex flex-wrap items-baseline justify-center gap-4">
                                <span
                                    className="font-serif text-5xl md:text-6xl text-marigold"
                                    data-testid="compat-pricing-final"
                                >
                                    {finalLabel}
                                </span>
                                <span
                                    className="font-serif text-2xl text-slate-500 line-through"
                                    data-testid="compat-pricing-list"
                                >
                                    {listLabel}
                                </span>
                                <span
                                    className="px-2.5 py-1 rounded-sm bg-emerald-500/15 text-emerald-300 border border-emerald-500/30 text-xs font-bold uppercase tracking-wider"
                                    data-testid="compat-pricing-badge"
                                >
                                    {price.discount_pct}% off
                                </span>
                            </div>
                        ) : (
                            <div className="font-serif text-5xl md:text-6xl text-parchment" data-testid="compat-pricing-final">
                                {finalLabel || '—'}
                            </div>
                        )}

                        <p className="mt-4 text-sm text-slate-400">
                            One-time payment · No recurring charges · Refunded if the engine cannot resolve the chart
                        </p>
                    </div>

                    <ul className="mt-8 grid sm:grid-cols-2 gap-3 text-sm text-slate-300 max-w-xl mx-auto">
                        {[
                            'Full 5-layer doctrinal reading',
                            'Verdict + per-layer thumb signals',
                            'Partner-nature view from both sides',
                            'Branded shareable PNG (WhatsApp / Insta)',
                        ].map((b) => (
                            <li key={b} className="flex items-start gap-2">
                                <Check size={16} className="text-marigold mt-0.5 shrink-0" /> {b}
                            </li>
                        ))}
                    </ul>

                    <div className="mt-10 flex flex-col sm:flex-row gap-3 justify-center max-w-md mx-auto">
                        <Link to="/signup" className="flex-1" data-testid="compat-pricing-cta">
                            <Button className="w-full bg-marigold hover:bg-marigold-hover text-obsidian rounded-sm h-12 text-base">
                                Sign up &amp; run the reading
                            </Button>
                        </Link>
                    </div>

                    <p className="mt-5 text-[11px] text-slate-500 uppercase tracking-widest text-center">
                        {price?.currency === 'USD' ? 'USD via PayPal' : 'INR billing · UPI · Cards'}
                    </p>
                </Card>
            </div>
        </section>
    );
}

function FinalCta() {
    return (
        <section
            className="relative z-10 py-20 md:py-28 border-t border-marigold/10"
            data-testid="compat-final-cta"
        >
            <div className="max-w-3xl mx-auto px-6 text-center">
                <h2 className="font-serif text-4xl md:text-5xl text-parchment leading-tight">
                    Two charts. <span className="text-marigold italic">One honest answer.</span>
                </h2>
                <p className="mt-5 text-slate-400 leading-relaxed max-w-xl mx-auto">
                        Stop guessing. Stop scrolling Instagram match calculators. Sit with the chart for two minutes
                        and walk out knowing exactly what you are walking into.
                </p>
                <div className="mt-10">
                    <Link to="/signup" data-testid="compat-final-cta-btn">
                        <Button className="bg-marigold hover:bg-marigold-hover text-obsidian rounded-sm h-12 px-8 text-base">
                            <Heart size={16} className="mr-2" /> Start my compatibility reading
                        </Button>
                    </Link>
                </div>
                <p className="mt-5 text-xs text-slate-500">
                    Free account · No credit card to sign up
                </p>
            </div>
        </section>
    );
}

function Footer() {
    return (
        <footer className="relative z-10 border-t border-marigold/10 py-10">
            <div className="max-w-7xl mx-auto px-6 flex flex-col md:flex-row items-center justify-between gap-4">
                <div className="flex items-center gap-3">
                    <span className="font-serif text-parchment text-lg">AstroConsult</span>
                    <span className="text-xs text-slate-600">· Vedic KP Jyotish · 2026</span>
                </div>
                <div className="text-xs text-slate-600">
                    For reflection and guidance — not medical or legal advice.
                </div>
            </div>
        </footer>
    );
}
